import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { currentSelectedLanguage } from "../../../../../StateManagement/actions";

export function ImportGroupsModal({ isOpen, onClose, onImport, existingGroups = [], onOpenValidationPopup, showAlert }) {
  const selectedLanguage = useSelector(currentSelectedLanguage);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
    }
  };

  const processFile = async (fileToProcess) => {
    const file = fileToProcess || selectedFile;
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      if (showAlert) {
        showAlert(selectedLanguage?.LstLanguageCodes?.UI_IncorrectFileType, "warning");
      }
      setSelectedFile(null);
      return;
    }
    setIsProcessing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      const rawHeaders = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];
      const headerStrings = Array.isArray(rawHeaders) ? rawHeaders.map((h) => String(h).trim().toLowerCase()) : [];
      const isContactsTemplate = headerStrings.includes("email") && (headerStrings.includes("first name") || headerStrings.includes("last name") || !headerStrings.includes("group name"));
      const isDomainsTemplate = headerStrings.some((h) => ["domain", "domains", "domain name"].includes(h)) && !headerStrings.includes("group name");
      const hasGroupHeader = headerStrings.some((h) => ["group name", "group email", "groupname", "alias email", "dl email", "group_email"].includes(h));
      const isValidGroupFile = hasGroupHeader && !isContactsTemplate && !isDomainsTemplate;

      if (!isValidGroupFile) {
        if (showAlert) {
          showAlert(selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValidFile, "warning");
        }
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setIsProcessing(false);
        return;
      }

      if (!rows || rows.length === 0) {
        onClose();
        if (onOpenValidationPopup) {
          onOpenValidationPopup({
            missingRequiredFields: [{ value: "", issue: selectedLanguage?.LstLanguageCodes?.UI_GroupNameOrEmailAddressCannotBeEmpty }]
          });
        } else if (showAlert) {
          showAlert(selectedLanguage?.LstLanguageCodes?.UI_GroupNameOrEmailAddressCannotBeEmpty, "warning");
        }
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setIsProcessing(false);
        return;
      }

      const existingGroupEmailSet = new Set((existingGroups || []).map((g) => (g.aliasEmail || g.email || g.name || "").toLowerCase().trim()).filter(Boolean));
      const existingGroupNameSet = new Set((existingGroups || []).map((g) => (g.name || "").toLowerCase().trim()).filter(Boolean));
      const seenEmailsInFile = new Set();
      const seenNamesInFile = new Set();
      const validGroupList = [];
      let duplicateFound = false;
      const emailLeanghtValidation = [];
      const invalidEmailsWithoutDotCom = [];
      const longDescription = [];
      const invalidDomain = [];
      const emailRegex = /^[\w.-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
      const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
      let totalGroupsInFileCount = 0;

      rows.forEach((row) => {
        const findField = (...names) => {
          for (const n of names) {
            const match = Object.keys(row).find((k) => {
              if (!k) return false;
              const cleanK = k.trim().toLowerCase();
              const cleanN = n.toLowerCase();
              return (
                cleanK === cleanN ||
                cleanK.replace(/^\+/, "").trim() === cleanN.replace(/^\+/, "").trim() ||
                cleanK.replace(/\s+/g, "_") === cleanN.replace(/\s+/g, "_")
              );
            });
            if (match && row[match] !== undefined && row[match] !== null && String(row[match]).trim() !== "") {
              return String(row[match]).trim();
            }
          }
          return "";
        };

        const groupName = findField("group name", "name", "groupname", "title");
        const aliasEmail = findField("group email", "dl email", "alias email", "email", "group_email", "dlemail");
        const description = findField("description", "desc", "notes");
        const distListRaw = findField("distribution list", "isdistributionlist", "dl");
        const autoAddRaw = findField("auto add users", "autoaddusers", "auto add");
        const domainsRaw = findField("allowed domains", "domains", "allowed_domains");
        const membersRaw = findField("member emails", "members", "member_emails", "contacts");
        if (!groupName && !aliasEmail) return;
        totalGroupsInFileCount++;
        const lowerEmail = aliasEmail ? aliasEmail.toLowerCase().trim() : "";
        const lowerName = groupName ? groupName.toLowerCase().trim() : "";
        let hasMistake = false;
        if (lowerEmail) {
          if (lowerEmail.length > 255) {
            emailLeanghtValidation.push({ email: lowerEmail });
            hasMistake = true;
          }
          if (!emailRegex.test(lowerEmail)) {
            invalidEmailsWithoutDotCom.push({ email: lowerEmail });
            hasMistake = true;
          }
        }
        if (description.trim().length > 264) {
          longDescription.push({ description: description.trim() });
          hasMistake = true;
        }
        if (domainsRaw) {
          const rawDomainsList = domainsRaw.split(/[\s,;]+/).map((d) => d.trim()).filter(Boolean);
          rawDomainsList.forEach((rawDom) => {
            const cleanDom = rawDom.toLowerCase().replace(/^@/, "");
            if (!cleanDom || !domainRegex.test(cleanDom)) {
              invalidDomain.push({ email: rawDom });
              hasMistake = true;
            }
          });
        }
        if (hasMistake) return;
        // Filter duplicates
        if ((lowerEmail && (existingGroupEmailSet.has(lowerEmail) || seenEmailsInFile.has(lowerEmail))) || (lowerName && (existingGroupNameSet.has(lowerName) || seenNamesInFile.has(lowerName)))) {
          duplicateFound = true;
          return;
        }
        if (lowerEmail) seenEmailsInFile.add(lowerEmail);
        if (lowerName) seenNamesInFile.add(lowerName);
        const isDistributionList = ["true", "yes", "1"].includes(distListRaw.toLowerCase());
        const autoAddUsers = ["true", "yes", "1"].includes(autoAddRaw.toLowerCase());
        const domains = domainsRaw ? domainsRaw.split(/[\s,;]+/).map((d) => d.trim().toLowerCase().replace(/^@/, "")).filter(Boolean) : [];
        const memberContactIds = membersRaw ? membersRaw.split(/[\s,;]+/).map((m) => m.trim().toLowerCase()).filter(Boolean) : [];
        validGroupList.push({
          name: groupName || aliasEmail.split("@")[0] || "Imported Group",
          aliasEmail: lowerEmail || `${groupName.replace(/\s+/g, "").toLowerCase()}@company.com`,
          description: description.trim(),
          isDistributionList,
          autoAddUsers,
          domains,
          memberContactIds,
          contactIds: memberContactIds
        });
      });

      if (totalGroupsInFileCount === 0) {
        onClose();
        if (onOpenValidationPopup) {
          onOpenValidationPopup({
            missingRequiredFields: [{ value: "", issue: selectedLanguage?.LstLanguageCodes?.UI_GroupNameOrEmailAddressCannotBeEmpty }]
          });
        } else if (showAlert) {
          showAlert(selectedLanguage?.LstLanguageCodes?.UI_GroupNameOrEmailAddressCannotBeEmpty, "warning");
        }
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setIsProcessing(false);
        return;
      }

      const hasAnyMistakes = emailLeanghtValidation.length > 0 || invalidEmailsWithoutDotCom.length > 0 || longDescription.length > 0 || invalidDomain.length > 0;
      if (hasAnyMistakes) {
        onClose();
        if (onOpenValidationPopup) {
          onOpenValidationPopup({ emailLeanghtValidation, invalidEmailsWithoutDotCom, longDescription, invalidDomain });
        }
        setIsProcessing(false);
        return;
      }
      onClose();
      if (onImport) { onImport(validGroupList, duplicateFound); }
    } catch (err) {
      if (showAlert) {
        showAlert("Error parsing file.", "warning");
      }
      setSelectedFile(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadSample = (format = "csv") => {
    const headers = [["Group Name", "Group Email", "Description", "Distribution List", "Auto Add Users", "Allowed Domains", "Member Emails"]];
    const ws = XLSX.utils.aoa_to_sheet(headers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Groups_Template");
    if (format === "xlsx") {
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, "Groups_Template.xlsx");
    } else {
      const wbout = XLSX.write(wb, { bookType: "csv", type: "array" });
      const blob = new Blob([wbout], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "Groups_Template.csv");
    }
  };

  const handleConfirmImport = () => {
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  return (
    <div className="modalCus contacts-modal-show contacts-modal-wrapper">
      <div className="modal-dialog modal-dialog-centered contacts-modal-dialog-import">
        <div className="modal-content border-r4">
          {/* Header */}
          <div className="modal-header d-flex align-items-center justify-content-between">
            <h4 className="mb-0 text-danger fw-bold font-20">
              {selectedLanguage?.LstLanguageCodes?.UI_ImportContactGroups}
            </h4>
            <button type="button" className="btn btn-outline-danger Readerclose modalclose download" onClick={onClose}>
              <span className="material-icons font-20">highlight_off</span>
            </button>
          </div>

          {/* Body */}
          <div className="modal-body p-3">
            {/* Download Sample XLS / CSV Bar */}
            <div className="d-flex align-items-center mb-3">
              <span className="font-14 font-w500 text-dark">
                {selectedLanguage?.LstLanguageCodes?.UI_PleaseDownloadTheXLSorCSV}
              </span>
              <img src={"assets/img/icons/CSVdownload.png"} className="contacts-sample-download-img ms-3" alt="Download CSV" title="Download sample CSV" onClick={() => handleDownloadSample("csv")} />
              <img src={"assets/img/icons/XLSdownload.png"} className="contacts-sample-download-img ms-2" alt="Download XLS" title="Download sample XLS" onClick={() => handleDownloadSample("xlsx")} />
            </div>

            {/* Drag and Drop Zone */}
            <div className={`drag upload form-control contacts-dropzone ${dragActive ? "active-drop" : ""}`} onDragEnter={handleDrag} onDragLeave={handleDrag}
              onDragOver={handleDrag} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}>
              <div className="d-flex flex-column align-items-center justify-content-center p-3 text-center">
                <input type="file" ref={fileInputRef} className="contacts-hidden-file-input" accept=".csv,.xlsx,.xls" onChange={handleFileInputChange} />
                <span className="material-icons font-32 text-danger mb-1">
                  cloud_upload
                </span>
                <span className="font-14 font-w500 text-dark pb-1">
                  {selectedFile ? selectedFile.name : selectedLanguage?.LstLanguageCodes?.UI_DragandDropfilesHere}
                </span>
                <span className="text-muted font-12 pb-1">
                  {selectedLanguage?.LstLanguageCodes?.UI_or}
                </span>
                <span className="text-danger underline font-13 font-w600">
                  {selectedLanguage?.LstLanguageCodes?.UI_ClickAndSelectFiles}
                </span>
              </div>
            </div>

            {isProcessing && (
              <div className="text-center py-2 text-secondary font-13">
                Processing file, please wait...
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer d-flex align-items-center justify-content-end">
            <button type="button" title={selectedLanguage?.LstLanguageCodes?.UI_Cancel} className="btn btn-danger px-2 me-2 ms-1 w-140p truncate" onClick={onClose}>
              {selectedLanguage?.LstLanguageCodes?.UI_Cancel}
            </button>
            <button type="button" title={selectedLanguage?.LstLanguageCodes?.UI_Import} className="btn btn-primary px-2 ms-1 w-140p truncate" disabled={!selectedFile || isProcessing} onClick={handleConfirmImport}>
              {selectedLanguage?.LstLanguageCodes?.UI_Import}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImportGroupsModal;